import { useState } from 'react'
import { createRoute, redirect } from '@tanstack/react-router'
import { rootRoute } from './__root'
import { ThemePicker } from '@/components/ThemePicker'
import { BusswerksLogo } from '@/components/BusswerksLogo'
import { API_BASE, apiClient } from '@/lib/api'
import { startNativeLogin } from '@/lib/nativeAuth'
import { usePlatform } from '@/hooks/usePlatform'

export const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: async () => {
    try {
      await apiClient.get('/api/v1/auth/me')
      throw redirect({ to: '/home' })
    } catch (err) {
      // Re-throw redirects so TanStack Router can handle them; swallow
      // any other error (including 401) so the login page renders.
      if (err && typeof err === 'object' && 'to' in err) throw err
    }
  },
  component: LoginPage,
})

function LoginPage() {
  const platform = usePlatform()
  // Surface any sign-in error to the UI. The native PKCE flow can fail
  // on multiple axes — missing `VITE_WORKOS_CLIENT_ID`, AuthSession
  // plugin not registered, WorkOS rejecting the code exchange — all
  // of which used to silently go to `void` and give the user a dead
  // button. Better to render the error.
  const [error, setError] = useState<string | null>(null)
  const [signingIn, setSigningIn] = useState(false)

  const signIn = async () => {
    setError(null)
    if (platform.isNative) {
      // Runs the PKCE flow end-to-end: generates verifier/challenge,
      // opens ASWebAuthenticationSession via our custom AuthSession
      // plugin, exchanges the returned code with WorkOS, persists the
      // tokens to Keychain, then hard-navigates to /home.
      try {
        setSigningIn(true)
        await startNativeLogin()
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err)
        console.error('[login] native sign-in failed:', err)
        setError(msg)
      } finally {
        setSigningIn(false)
      }
      return
    }
    // Pass the origin so the API can build a redirect URI and
    // post-login redirect that match the host the user is on
    // (e.g. custom domain instead of the canonical CloudFront URL).
    //
    // Prefix with API_BASE so localhost dev hits the deployed dev API
    // (VITE_API_URL=https://dev.busswerks.io) instead of Vite's 404
    // page. When API_BASE is empty (production, same-origin), this
    // degrades to the historical `/api/v1/auth/login` path.
    const origin = encodeURIComponent(window.location.origin)
    window.location.href = `${API_BASE}/api/v1/auth/login?origin=${origin}`
  }

  return (
    <div className="min-h-[100dvh] pt-safe pb-safe flex items-center justify-center bg-gradient-to-br from-surface-base to-surface-sunken">
      <div className="absolute top-4 right-4 pt-safe">
        <ThemePicker />
      </div>
      <div className="w-full max-w-md">
        <div className="bg-surface-raised rounded-xl shadow-high p-8 border border-brd-subtle">
          <div className="flex justify-center mb-2">
            <BusswerksLogo size="md" className="text-content-primary" />
          </div>
          <p className="text-content-secondary text-center mb-8">
            Sign in or create a free account
          </p>

          <button
            onClick={() => void signIn()}
            disabled={signingIn}
            className="w-full btn-primary btn-lg font-semibold disabled:opacity-60"
          >
            {signingIn ? 'Signing in…' : 'Continue with AuthKit'}
          </button>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive break-words"
            >
              <div className="font-semibold mb-0.5">Sign-in failed</div>
              <div className="font-mono text-[11px] leading-snug">{error}</div>
            </div>
          )}

          <p className="text-center text-content-tertiary text-xs mt-6">
            New here? No problem &mdash; you can create a free account on the
            next screen. Existing users will be signed in automatically.
          </p>
        </div>
      </div>
    </div>
  )
}
